import qsLib from 'qs';

import { z as zOrigin } from 'zod';
export { zOrigin as z };

type RouteDefinition<R extends Record<string, zOrigin.ZodTypeAny>> = {
  [K in keyof R]: R[K] extends zOrigin.ZodType<infer T, any, any>
    ? {
        href: (params: T) => string;
        dynamicMetadata?: (params: T) => Promise<DynamicMetadata | null>;
        staticMetadata?: StaticMetadata | null;
      }
    : {
        href: () => string;
        dynamicMetadata?: () => Promise<DynamicMetadata | null>;
        staticMetadata?: StaticMetadata | null;
      };
};

function invariant(condition: any, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

/**
 *
 * Define routes using Zod schemas.
 *```ts
 * const routeBuilder = defineParams({
 *   '/user-profile': z.object({ userId: z.number() }),
 *   '/home': z.undefined(),
 * });
 *```
 *
 * Create routes configuration.
 * The `href` method defines the URL structure.
 * Optional `metadata` method can be used to generate metadata for each route.
 * ```ts
 * const routes = routeBuilder({
 *  '/user-profile': {
 *    href: (params) => `/user-profile?userId=${params.userId}`,
 *  },
 *  '/home': {
 *    href: () => `/home`,
 *  },
 * });
 *```
 *
 * Usage with Next.js Link component.
 * @example
 * ```tsx
 * <Link href={routes.href('/user-profile', { userId: 123 })}>User Profile</Link>;
 * ```
 *
 * Parsing query object in a Next.js page component.
 * @example
 * ```tsx
 * const { query } = useRouter();
 * const params = routes.queryParser('/user-profile', query);
 * // params now has the shape defined by the Zod schema and is validated.
 * ```
 */
export function defineParams<R extends Record<string, zOrigin.ZodTypeAny>>(routeDefinitions: R) {
  function routeBuilder(config: RouteDefinition<R>) {
    function getRouteInfo<Key extends keyof R>(path: Key) {
      const routePathInfo = config[path];
      invariant(routePathInfo, `RoutePath: ${path as string} is not defined`);

      return routePathInfo;
    }

    function getPathPrefixType<Key extends keyof R>(
      path: Key,
      params?: zOrigin.input<R[Key]>,
      staticMetadata?: StaticMetadata,
    ): string {
      return '';
    }

    let getPathPrefix: typeof getPathPrefixType = getPathPrefixType;

    return {
      setPathPrefix: (_getPathPrefix: typeof getPathPrefix) => {
        getPathPrefix = _getPathPrefix;
      },
      paramsType<Key extends keyof R>(path: Key) {
        return routeDefinitions[path] as any as zOrigin.infer<R[Key]>;
      },
      queryParser<Key extends keyof R, Data extends zOrigin.infer<R[Key]>>(
        path: Key,
        queryObj: any,
      ): { success: true; data: Data } | { success: false } {
        const parsed = routeDefinitions[path].safeParse(queryObj);

        if (parsed.success) {
          return {
            success: true,
            data: parsed.data,
          };
        }

        return {
          success: false,
        };
      },

      queryStringParser<Key extends keyof R, Data extends zOrigin.infer<R[Key]>>(
        _path: Key,
        queryString: string,
      ): Partial<Data> {
        return qsParse(queryString) as Partial<Data>;
      },

      href<Key extends keyof R>(path: Key, params?: zOrigin.input<R[Key]>): string {
        const routeInfo = getRouteInfo(path);

        const parsed = routeDefinitions[path].safeParse(params ?? {}) ?? {};
        try {
          const pathPrefix = getPathPrefix(
            path,
            (parsed as any).data as any,
            routeInfo.staticMetadata || undefined,
          );

          return pathPrefix + routeInfo.href((parsed as any).data as any);
        } catch (e) {
          console.log(e);
          return '';
        }
      },

      staticMetadata: <Key extends keyof R>(path: Key): StaticMetadata | null => {
        const routeInfo = getRouteInfo(path);

        if (!routeInfo.staticMetadata) {
          return null;
        }

        return routeInfo.staticMetadata;
      },

      dynamicMetadata: async <Key extends keyof R>(
        path: Key,
        params?: zOrigin.input<R[Key]>,
      ): Promise<DynamicMetadata | null> => {
        const routeInfo = getRouteInfo(path);

        if (!routeInfo.dynamicMetadata) {
          return null;
        }

        const parsed = routeDefinitions[path].parse(params);
        return await routeInfo.dynamicMetadata(parsed);
      },
    };
  }
  return routeBuilder;
}

export function qsStr(obj: any) {
  if (!obj) {
    return '';
  }
  return qsLib.stringify(obj, { addQueryPrefix: true, skipNulls: true });
}

export function qsParse(str: string) {
  return qsLib.parse(str, { ignoreQueryPrefix: true });
}

export type Service = 'lms' | 'cms' | 'crm' | 'b2c' | 'hub' | 'supervisor';
export interface DynamicMetadata {
  title?: null | string;
}
export interface StaticMetadata {
  services?: Service[];
  title?: string;
}
